import React from 'react';

import { Box, Card, CardBody, Divider, Flex, HStack, VStack } from '@chakra-ui/react';
import millify from 'millify';
import Link from 'next/link';
import { MdOutlineHomeWork, MdOutlineVideoCameraFront } from 'react-icons/md';

import RoundedBadge from '@components/custom-badge/RoundedBadge';
import { CustomText } from '@components/Custom-text';
import { IconRenderer } from '@components/IconComponent';
import { CompanyLogo } from '@components/logo/CompanyLogo';
import Rating from '@components/rating/Rating';
import { useAppDispatch } from '@hooks/store';
import { Mentor } from '@modules/auth/types';
import { clearBookingData } from '@modules/find-mentor/slice/bookingSlice';
import { paths } from '@routes/path';


interface MentorCardProps {
    mentorInfo: Mentor;
    width?: string;
}

const MentorCard = ({ mentorInfo, width }: MentorCardProps) => {
    const dispatch = useAppDispatch();
    const shouldShowSessions = mentorInfo?.sessionsCompleted > 0;
    const shouldShowReviews = mentorInfo?.totalReviews > 0;
    let badgeLabel = '';
    if (mentorInfo?.sessionsCompleted === 0) {
        badgeLabel = 'New';
    } else if (mentorInfo?.totalRating >= 4) {
        badgeLabel = 'Top Rated';
    }
    const shouldShowBadge = Boolean(badgeLabel);

      const handleCardClick = () => {
        dispatch(clearBookingData());
    };

    return (
        <Link onClick={handleCardClick} href={`${paths.general.findMentor}/${mentorInfo?.username}`}>
            <Card
                display="flex"
                width={width}
                variant="outline"
                size="sm"
                _hover={{
                    borderColor: 'primary.light',
                    boxShadow: '1px 1px 10px #DEDEDE',
                }}
                borderRadius={8}
            >
                <CardBody p="8px">
                    <VStack width="100%">
                        <Box
                            height="220px"
                            minH="220px"
                            width="100%"
                            position="relative"
                            borderRadius="8px"
                            style={{
                                backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.15),rgba(0, 0, 0, 0.45)), url(${mentorInfo.profileImageUrl})`,
                                backgroundSize: 'cover',
                                backgroundRepeat: 'no-repeat',
                                backgroundPosition: 'center',
                            }}
                        >
                            {shouldShowBadge ? (
                                <Box position="absolute" top="10px" right="14px">
                                    <RoundedBadge>{badgeLabel}</RoundedBadge>
                                </Box>
                            ) : null}
                            <VStack
                                alignItems="self-start"
                                gap={0}
                                position="absolute"
                                bottom="16px"
                                left="16px"
                                paddingRight={5}
                                paddingLeft={{ base: '10px', md: 0 }}
                            >
                                <CustomText color="white" weight={600} size="md">
                                    {mentorInfo?.name ?? '-'}
                                </CustomText>
                                <CustomText color="white" size="xs">
                                    <HStack>
                                        <CompanyLogo
                                            src={mentorInfo?.latestExperience?.logo?.url}
                                            height={24}
                                            width={24}
                                        />
                                        <CustomText color="white" size="sm" noOfLines={1}>
                                            {mentorInfo?.latestExperience?.company ?? '-'}
                                        </CustomText>
                                    </HStack>
                                </CustomText>
                            </VStack>
                        </Box>
                        <HStack minW={0} alignSelf="flex-start">
                            <div>
                                <MdOutlineHomeWork size={24} />
                            </div>
                            <CustomText color="mainText" size="sm" noOfLines={1}>
                                {mentorInfo?.latestExperience?.role ?? '-'}
                            </CustomText>
                        </HStack>
                        <HStack minW={0} alignSelf="flex-start">
                            <IconRenderer
                                iconName={mentorInfo.domain?.icon as string}
                                iconPack={mentorInfo.domain?.iconPack as string}
                            />
                            <CustomText color="mainText" size="sm" noOfLines={1}>
                                {mentorInfo?.domain?.name ?? '-'}
                            </CustomText>
                        </HStack>
                        <Flex
                            w="100%"
                            justifyContent={shouldShowSessions ? 'space-between' : 'flex-start'}
                        >
                            {shouldShowSessions ? (
                                <HStack minW={0} mr={1}>
                                    <div>
                                        <MdOutlineVideoCameraFront size={24} />
                                    </div>
                                    <CustomText color="mainText" size="sm">
                                        {millify(mentorInfo?.sessionsCompleted)} Session
                                        {mentorInfo?.sessionsCompleted === 1 ? '' : 's'}
                                    </CustomText>
                                </HStack>
                            ) : null}
                            <HStack minW={0} gap={1}>
                                <CustomText color="mainText" size="sm" as="span" weight={500}>
                                    Experience:
                                </CustomText>
                                <CustomText color="mainText" size="sm" as="span">
                                    {mentorInfo?.totalExperience?.years ?? 1} Year
                                    {(mentorInfo?.totalExperience?.years ?? 1) === 1 ? '' : 's'}
                                </CustomText>
                            </HStack>
                        </Flex>
                        {shouldShowReviews ? (
                            <Flex w="100%" justifyContent="space-between">
                                <HStack minW={0}>
                                    <CustomText color="mainText" size="sm">
                                        Reviews
                                    </CustomText>
                                </HStack>
                                <HStack minW={0} flex={1} ml={1} justifyContent="flex-end">
                                    <Rating rating={mentorInfo.totalRating ?? 0} />
                                    <CustomText color="mainText" size="sm">
                                        {millify(mentorInfo.totalReviews ?? 0)}
                                    </CustomText>
                                </HStack>
                            </Flex>
                        ) : null}
                        <Divider orientation="horizontal" />
                        <Flex
                            w="100%"
                            h={7}
                            alignItems="center"
                            justifyContent="center"
                            direction="column"
                        >
                            <CustomText color="primary.light" size="sm" noOfLines={2}>
                                Book a Session
                            </CustomText>
                        </Flex>
                    </VStack>
                </CardBody>
            </Card>
        </Link>
    );
};

export default MentorCard;
